import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import { companyChannel } from '../services/pusher/Channels';
import { PusherEventType, usePusher } from '../providers/PusherProvider';
import ReduxHooks from '../store/ReduxHooks';
import { actionCreators } from '../store/actions';
import { ReceiptType } from '../providers/PrintProvider';
import { useOngoingOrders } from '../providers/OngoingOrdersProvider';
import OrderHelpers from '../services/helpers/OrderHelpers';
import useFeedbackHandler from '../hooks/useFeedbackHandler';
import usePrinter from '../hooks/usePrinter';
import { useStats } from '../providers/StatsProvider';
import useSound from '../hooks/utility/useSound';

export default function CompanyChannel() {
  const { print } = usePrinter();
  const { playSound } = useSound();
  const { client, unbind } = usePusher();
  const { incrementRevenue } = useStats();
  const { setFeedback } = useFeedbackHandler({ renderToasts: true });
  const {
    addOrder,
    removeOrder,
    replaceOrderData,
    selectedOrder,
    setSelectedOrder,
    loadOrderDetails,
    toggleOrderDetailsModal,
  } = useOngoingOrders();

  const { company } = useSelector((state) => state.company);

  useEffect(() => {
    client && company?.id && connect();

    return () => cleanup();
  }, [client, company?.id]);

  async function connect() {
    client
      .subscribe(companyChannel(company.id))
      .bind(PusherEventType.OrderPaid, (data) => orderPaidHandler(data))
      .bind(PusherEventType.OrderRefunded, (data) => orderRefundedHandler(data))
      .bind(PusherEventType.OrderUpdated, (data) => orderUpdatedHandler(data))
      .bind(PusherEventType.PrintOrder, (data) => printOrderHandler(data))
      .bind(PusherEventType.OrderPickupTimeOptimized, (data) => orderPickupTimeOptimizedHandler(data))
      .bind(PusherEventType.OrderComplete, (data) => orderUpdatedHandler(data))
      .bind(PusherEventType.RefundCreated, (data) => refundCreatedHandler(data))
      .bind(PusherEventType.OrdersPaused, (data) => ordersPausedHandler(data))
      .bind(PusherEventType.OrdersUnpaused, () => ordersUnpausedHandler())
      .bind(PusherEventType.TerminalSettingsUpdates, (data) => terminalSettingsUpdatedHandler(data));
  }

  function cleanup() {
    unbind(
      [
        PusherEventType.OrderPaid,
        PusherEventType.OrderRefunded,
        PusherEventType.OrderUpdated,
        PusherEventType.PrintOrder,
        PusherEventType.OrderPickupTimeOptimized,
        PusherEventType.OrderComplete,
        PusherEventType.RefundCreated,
        PusherEventType.OrdersPaused,
        PusherEventType.OrdersUnpaused,
      ],
      companyChannel(company.id),
    );
  }

  const _loadOrderDetails = (id, callback) =>
    loadOrderDetails(id)
      .then(({ data }) => callback(data))
      .catch((error) =>
        setFeedback({
          type: 'error',
          message: error.message,
        }),
      );

  function orderPaidHandler(data) {
    _loadOrderDetails(data.id, (orderData) => {
      !orderData.is_dummy && incrementRevenue(orderData.total_amount);
      addOrder(orderData);

      if (company?.auto_accept_orders && orderData.is_manually_confirmed && orderData.is_pickup_time_optimized) {
        printOrderWithSound(orderData);
      }
    });
  }

  function orderRefundedHandler(data) {
    if (selectedOrder?.id === data.id) {
      toggleOrderDetailsModal();
    }

    if (!data.is_hidden_by_staff) {
      return;
    }

    removeOrder(data.id);
  }

  function orderUpdatedHandler(data, callback = null) {
    const _callback = callback || ((orderData) => replaceOrderData(orderData));

    _loadOrderDetails(data.id, _callback);
  }

  function printOrderHandler(data) {
    _loadOrderDetails(data.id, (orderData) => print({ type: ReceiptType.Order, payload: orderData }));
  }

  function orderPickupTimeOptimizedHandler(data) {
    _loadOrderDetails(data.id, (orderData) => {
      replaceOrderData(orderData);

      // External deliveries on the WaitForDriverAssignment strategy are printed when staff
      // press "Print & Confirm" on OrderPreparationReminderScreen. Skip the auto-print here
      // so they are not printed twice.
      if (!OrderHelpers.isPrintDeferredToDriverAssignment(orderData, company)) {
        printOrderWithSound(orderData);
      }
    });
  }

  function refundCreatedHandler(data) {
    _loadOrderDetails(data.order_id, (orderData) => {
      !orderData.is_dummy && incrementRevenue(-data.amount);
      replaceOrderData(orderData);

      if (data.order_id === selectedOrder?.id) {
        setSelectedOrder(orderData);
      }
    });
  }

  // Merged onto the current company, not set wholesale: `set` replaces the object, and dropping
  // the rest would take entitlements like has_external_printer_feature with it, silently moving
  // printing back to the built-in head until the next profile load. Only the pause window is
  // taken from the event: it is a company resource that was built without its relations, so
  // fields like `locales` and `service_provider` arrived as `{}` holes — persisted once, a
  // non-iterable `locales` crashed BrandProvider on every boot.
  function ordersPausedHandler(data) {
    ReduxHooks.dispatch(
      actionCreators.company.set({
        ...company,
        orders_paused_from: data.orders_paused_from,
        orders_paused_until: data.orders_paused_until,
        orders_pause_reason: data.orders_pause_reason,
      }),
    );
  }

  function ordersUnpausedHandler() {
    ReduxHooks.dispatch(
      actionCreators.company.set({
        ...company,
        orders_paused_from: null,
        orders_paused_until: null,
        orders_pause_reason: null,
      }),
    );
  }

  function terminalSettingsUpdatedHandler(data) {
    ReduxHooks.dispatch(
      actionCreators.company.set({
        ...company,
        ...data,
      }),
    );
  }

  function printOrderWithSound(data) {
    playSound();
    print({ type: ReceiptType.Order, payload: data });
  }

  return null;
}
